import { useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useClientDashboard, useClientBrands } from '../hooks/useCompleteness';
import { useValueSavings } from '@/features/value/hooks/useValueSavings';
import { ScoreRing } from '../components/ScoreRing';
import { CategoryCard, CategoryCardSkeleton } from '../components/CategoryCard';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { ProductList } from '../components/ProductList';
import { ViewToggle, type ViewMode } from '../components/ViewToggle';
import { BrandGrid } from '../components/BrandGrid';
import { gekozenMerk, kiesMerk, productenSleutels } from '../productLijst';
import { useLijstStandStore } from '@/stores/lijstStandStore';
import { ValueSummary } from '@/features/value/components/ValueSummary';
import { ManagementSummaryCTA } from '@/features/value/components/ManagementSummaryCTA';
import { BenchmarkBar } from '@/features/value/components/BenchmarkBar';
import { IncompleteCostCard } from '@/features/value/components/IncompleteCostCard';
import { useIncompleteCost } from '@/features/value/hooks/useIncompleteCost';
import { useClientId } from '@/features/value/hooks/useClientId';
import { CalculatedAtCaption } from '@/features/data-quality/components/CalculatedAtCaption';
import { TrendPanel } from '@/features/data-quality/components/TrendPanel';
import { BatchStatusPanel } from '@/features/data-quality/components/BatchStatusPanel';
import { AlertsPanel } from '@/features/data-quality/components/AlertsPanel';
import { MissingGpcReportPage } from '@/features/data-quality/pages/MissingGpcReportPage';
import type { BrandOption } from '../completeness.api';

function getCurrentQuarter(): { quarter: string; year: number; label: string } {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3) + 1;
  const year = now.getFullYear();
  return { quarter: `Q${q}`, year, label: `Q${q} ${year}` };
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { clientId: routeClientId } = useParams<{ clientId: string }>();
  const clientId = useClientId();

  // Het merk van het dashboard ís het vaste merk van de productenlijst eronder (paginafilter), geen eigen stand.
  const selectedBrandId = gekozenMerk(useLijstStandStore((s) => s.standen[productenSleutels(clientId).pagina]?.filters));
  const [viewMode, setViewMode] = useState<ViewMode>('products');

  // Een merkklik opent de productenlijst van deze klant, vast op dat merk (besluit Friso 27-09-2026).
  const handleBrandClick = useCallback((brand: BrandOption) => {
    kiesMerk(clientId, brand.id);
    setViewMode('products');
  }, [clientId]);

  const { data, isLoading, isError, error, refetch } = useClientDashboard(
    clientId,
    selectedBrandId ? { brandId: selectedBrandId } : undefined,
  );
  const { data: brandsData } = useClientBrands(clientId);
  const { data: savingsData, isLoading: savingsLoading } = useValueSavings(clientId);
  const { data: incompleteCostData } = useIncompleteCost(clientId);

  const quarterInfo = useMemo(() => getCurrentQuarter(), []);

  // Alle hooks staan hierboven: een hook achter een vroege return wordt voorwaardelijk aangeroepen,
  // en dan crasht React zodra de pagina van foutstaat naar gevulde staat gaat.
  if (isError) {
    return (
      <div data-testid="dashboard-page">
        <ApiErrorMessage error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  const dashboard = data?.data;
  const brands = brandsData?.data ?? [];
  const savings = savingsData?.data;

  const totalProducts = dashboard?.totalProducts ?? 0;
  const overallScore = dashboard?.overallScore ?? 0;
  const completeCount = totalProducts > 0
    ? Math.round(totalProducts * (overallScore / 100))
    : 0;
  const incompleteCount = totalProducts - completeCount;
  const completePercent = totalProducts > 0
    ? Math.round((completeCount / totalProducts) * 100)
    : 0;

  const formatEur = (amount: number) =>
    amount.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

  return (
    <div data-testid="dashboard-page" className="space-y-5">
      {/* Page header with brand filter */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1
            style={{
              fontSize: '1.25rem',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'var(--foreground-heading)',
            }}
          >
            {t('dashboard.title')}
          </h1>
          <p
            className="mt-[3px]"
            style={{ fontSize: '0.8125rem', color: 'var(--foreground-muted)' }}
          >
            {t('dashboard.subtitle')}
          </p>
        </div>

        {/* Brand filter */}
        {brands.length > 0 && (
          <select
            data-testid="brand-filter"
            value={selectedBrandId}
            onChange={(e) => {
              kiesMerk(clientId, e.target.value || null);
            }}
            className="rounded-lg px-3 py-2 text-sm"
            style={{
              backgroundColor: 'var(--background)',
              border: '1px solid var(--border)',
              color: 'var(--foreground)',
            }}
          >
            <option value="">{t('dashboard.allBrands')}</option>
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* h4-2/FR23/FR25: trend + versheid + batch-status (internal-only view, routeClientId always set here) */}
      {routeClientId && !isLoading && dashboard && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <TrendPanel gln={routeClientId} />
            <CalculatedAtCaption isoDate={dashboard.lastUpdated} />
          </div>
        </div>
      )}
      {routeClientId && <BatchStatusPanel gln={routeClientId} />}

      {/* h4-3/FR24/FR27: alerts + missing-GPC report (internal-only, per-client) */}
      {routeClientId && <AlertsPanel gln={routeClientId} />}
      {routeClientId && <MissingGpcReportPage gln={routeClientId} />}

      {/* Summary Cards */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton h-24 rounded-xl" />
          ))
        ) : (
          <>
            <DashboardSummaryCard
              title={t('dashboard.totalCompleteness')}
              value={`${overallScore}%`}
              icon="insert_chart"
              iconColor="var(--primary)"
              iconBg="var(--primary-light)"
            />
            <DashboardSummaryCard
              title={t('dashboard.totalProducts')}
              value={totalProducts.toLocaleString('nl-NL')}
              icon="inventory_2"
              iconColor="var(--primary)"
              iconBg="var(--primary-light)"
            />
            {savingsLoading ? (
              <>
                <div className="skeleton h-24 rounded-xl" />
                <div className="skeleton h-24 rounded-xl" />
              </>
            ) : (
              <>
                <DashboardSummaryCard
                  title={t('dashboard.hoursSaved')}
                  value={savings
                    ? `${Math.round(savings.timeSaving.hoursSavedPerQuarter)} ${t('dashboard.hourUnit')}`
                    : t('dashboard.savingsCalculating')
                  }
                  icon="schedule"
                  iconColor="var(--secondary)"
                  iconBg="var(--secondary-light)"
                  borderColor="var(--secondary)"
                  quarterLabel={savings ? savings.quarter : quarterInfo.label}
                />
                <DashboardSummaryCard
                  title={t('dashboard.costSaved')}
                  value={savings
                    ? formatEur(savings.totalSavingEur)
                    : t('dashboard.savingsCalculating')
                  }
                  icon="savings"
                  iconColor="var(--success-dark)"
                  iconBg="var(--success-light)"
                  borderColor="var(--success-dark)"
                  quarterLabel={savings ? savings.quarter : quarterInfo.label}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* Incompleteness Cost Card */}
      {incompleteCostData?.data && incompleteCostData.data.totalMissingFields > 0 && (
        <IncompleteCostCard
          data={incompleteCostData.data}
          savingsPath={routeClientId ? `/data-quality/${routeClientId}/reports/savings` : '/reports/savings'}
        />
      )}

      {/* Category Cards */}
      <div className="grid gap-3.5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {isLoading
          ? Array.from({ length: 5 }).map((_, i) => (
              <CategoryCardSkeleton key={i} />
            ))
          : dashboard?.categories.map((cat) => (
              <CategoryCard
                key={cat.categoryId}
                category={cat}
                clientId={clientId}
                incompleteCount={cat.incompleteCount}
                completeCount={cat.filledCount}
                totalCount={cat.fieldCount}
              />
            ))}
      </div>

      {/* Value Summary Section */}
      {!isLoading && dashboard && (
        <>
          <hr style={{ border: 'none', borderTop: '2px solid var(--border)', margin: '0.5rem 0' }} />
          <div
            style={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: 'var(--foreground-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
            }}
          >
            {t('value.sectionTitle')}
          </div>
          <ValueSummary clientId={clientId} isEmployee={!!routeClientId} />
          <ManagementSummaryCTA clientId={clientId} isEmployee={!!routeClientId} />
          <BenchmarkBar clientId={clientId} />
        </>
      )}

      {/* Product summary line */}
      {!isLoading && dashboard && (
        <p
          style={{
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: 'var(--foreground-muted)',
          }}
        >
          {t('dashboard.productSummary', { complete: completeCount, total: totalProducts })}
        </p>
      )}

      {/* Product/Brand section */}
      {!isLoading && dashboard && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h2
              style={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'var(--foreground-heading)',
              }}
            >
              {t('products.title')}
            </h2>
            <ViewToggle activeView={viewMode} onViewChange={setViewMode} />
          </div>

          {/* Brand Grid View */}
          {viewMode === 'grid' && (
            <BrandGrid
              brands={brands}
              isLoading={false}
              isError={false}
              error={null}
              onRetry={() => {}}
              layout="grid"
              onBrandClick={handleBrandClick}
            />
          )}

          {/* Brand List View */}
          {viewMode === 'brand-list' && (
            <BrandGrid
              brands={brands}
              isLoading={false}
              isError={false}
              error={null}
              onRetry={() => {}}
              layout="list"
              onBrandClick={handleBrandClick}
            />
          )}

          {/* Product Table View */}
          {viewMode === 'products' && (
            <ProductList categories={dashboard?.categories} clientId={clientId} key={clientId} />
          )}
        </div>
      )}
    </div>
  );
}

function DashboardSummaryCard({
  title,
  value,
  subtitle,
  icon,
  iconColor,
  iconBg,
  borderColor,
  quarterLabel,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: string;
  iconColor: string;
  iconBg: string;
  borderColor?: string;
  quarterLabel?: string;
}) {
  return (
    <div
      className="flex items-start justify-between rounded-xl px-5 py-4"
      style={{
        backgroundColor: 'var(--background)',
        border: '1px solid var(--border-soft)',
        borderLeft: borderColor ? `3px solid ${borderColor}` : '1px solid var(--border-soft)',
        boxShadow: 'var(--card-shadow)',
      }}
    >
      <div>
        <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>{title}</p>
        <p className="mt-1 text-2xl font-bold" style={{ color: 'var(--foreground)' }}>{value}</p>
        {subtitle && (
          <p className="mt-0.5 text-[0.6875rem]" style={{ color: 'var(--foreground-muted)' }}>{subtitle}</p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1">
        <div
          className="flex h-9 w-9 items-center justify-center rounded-lg"
          style={{ backgroundColor: iconBg }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '20px', color: iconColor }}>
            {icon}
          </span>
        </div>
        {quarterLabel && (
          <span
            className="text-[0.625rem]"
            style={{ color: 'var(--foreground-muted)' }}
          >
            {quarterLabel}
          </span>
        )}
      </div>
    </div>
  );
}
